"use client";

import type { PortalRole } from "@/lib/navigation/portal-nav";
import { createContext, useContext } from "react";

/**
 * Server-derived facts the portal sidebar needs (portal IA overhaul), set once
 * per navigation by AuthenticatedContent off the same cache()d entitlement
 * row everything else reads. Everything here is presentation: the pages and
 * server actions enforce their own gates regardless of what the sidebar shows.
 */
export interface PortalNavFacts {
  role: PortalRole;
  /** The viewer's organization name (null for platform accounts). */
  orgName: string | null;
  /** The org's web-chat opt-in — the "Open chat" entry in Web Chat (legacy). */
  chatEnabled: boolean;
  /** Where "Open chat" goes: "/chat", or the absolute chat-host URL when the split is active. */
  chatHref: string;
  voiceEnabled: boolean;
  /** FUNDING_CODES_ENABLED: Transfer › Transfer is offered. */
  fundingCodesEnabled: boolean;
  /** CLIENT_INVITES_ENABLED: Transfer › Invite is offered. */
  clientInvitesEnabled: boolean;
}

const DEFAULT_FACTS: PortalNavFacts = {
  role: "member",
  orgName: null,
  chatEnabled: false,
  chatHref: "/chat",
  voiceEnabled: false,
  fundingCodesEnabled: false,
  clientInvitesEnabled: false,
};

const PortalNavContext = createContext<PortalNavFacts>(DEFAULT_FACTS);

export function PortalNavProvider({ facts, children }: { facts: PortalNavFacts; children: React.ReactNode }) {
  return <PortalNavContext value={facts}>{children}</PortalNavContext>;
}

export function usePortalNavFacts(): PortalNavFacts {
  return useContext(PortalNavContext);
}

